import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Form,
  Input,
  Select,
  Button,
  Space,
  Spin,
  Alert,
  message,
  Card,
  InputNumber,
} from 'antd';
import {
  ArrowLeftOutlined,
  PlusOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import {
  getWorkflowDefinition,
  createWorkflowDefinition,
  updateWorkflowDefinition,
  type CreateWorkflowDefinitionDto,
  type UpdateWorkflowDefinitionDto,
  type WorkflowStepDefinition,
} from '@/api/workflowApi';
import { useAuth } from '@/hooks/useAuth';

const { TextArea } = Input;

const TRIGGER_TYPE_OPTIONS = ['manual', 'event', 'scheduled'];
const STEP_TYPE_OPTIONS = ['action', 'condition', 'delay', 'notification'];
const STEP_ACTION_OPTIONS = [
  'send_email',
  'update_field',
  'create_task',
  'webhook',
  'wait',
  'notify',
];

interface StepFormValues {
  name: string;
  type: string;
  action: string;
  on_success?: number | null;
  on_failure?: number | null;
}

interface DefinitionFormValues {
  name: string;
  description?: string;
  trigger_type: string;
  tags?: string[];
  steps: StepFormValues[];
}

export default function WorkflowDefinitionForm() {
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [form] = Form.useForm<DefinitionFormValues>();

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canWrite = hasPermission('workflows', 'write');

  useEffect(() => {
    if (!isEdit || !id) return;
    setLoading(true);
    setError(null);
    getWorkflowDefinition(id)
      .then((def) => {
        form.setFieldsValue({
          name: def.name,
          description: def.description || undefined,
          trigger_type: def.trigger_type,
          tags: def.tags?.length ? def.tags : undefined,
          steps: def.steps?.map((s) => ({
            name: s.name,
            type: s.type,
            action: s.action,
            on_success: s.on_success,
            on_failure: s.on_failure,
          })) ?? [],
        });
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : t('app.not_found'))
      )
      .finally(() => setLoading(false));
  }, [isEdit, id, form, t]);

  const handleSubmit = async (values: DefinitionFormValues) => {
    setSubmitting(true);
    setError(null);
    try {
      const steps: WorkflowStepDefinition[] = (values.steps ?? []).map(
        (s, idx) => ({
          step_index: idx,
          name: s.name,
          type: s.type,
          action: s.action,
          config: {},
          conditions: {},
          on_success: s.on_success ?? null,
          on_failure: s.on_failure ?? null,
        })
      );

      if (isEdit && id) {
        const data: UpdateWorkflowDefinitionDto = {
          name: values.name,
          description: values.description,
          trigger_type: values.trigger_type,
          steps,
          tags: values.tags,
        };
        await updateWorkflowDefinition(id, data);
        message.success(t('workflows.update_success'));
        navigate(`/workflows/definitions/${id}`);
      } else {
        const data: CreateWorkflowDefinitionDto = {
          name: values.name,
          description: values.description,
          trigger_type: values.trigger_type,
          steps,
          tags: values.tags,
        };
        await createWorkflowDefinition(data);
        message.success(t('workflows.create_success'));
        navigate('/workflows');
      }
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : t('app.error');
      setError(errorMsg);
      message.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (!canWrite)
    return <Alert type="warning" message={t('app.forbidden')} />;

  if (loading)
    return (
      <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />
    );

  if (error && isEdit) {
    return (
      <div>
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/workflows')}
        >
          {t('app.back')}
        </Button>
        <Alert type="error" message={error} style={{ marginTop: 16 }} />
      </div>
    );
  }

  return (
    <div>
      <h2>
        {isEdit
          ? t('workflows.edit_title')
          : t('workflows.create_title')}
      </h2>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      <Form<DefinitionFormValues>
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        scrollToFirstError
        style={{ maxWidth: 900 }}
        initialValues={{ trigger_type: 'manual', steps: [] }}
      >
        <Form.Item
          label={t('workflows.field.name')}
          name="name"
          rules={[
            { required: true, message: t('workflows.validation.name_required') },
            { max: 200, message: t('workflows.validation.name_max_length') },
          ]}
        >
          <Input placeholder={t('workflows.field.name')} maxLength={200} />
        </Form.Item>

        <Form.Item
          label={t('workflows.field.description')}
          name="description"
        >
          <TextArea
            rows={3}
            placeholder={t('workflows.field.description')}
            maxLength={2000}
          />
        </Form.Item>

        <Form.Item
          label={t('workflows.field.trigger_type')}
          name="trigger_type"
          rules={[
            { required: true, message: t('workflows.validation.trigger_type_required') },
          ]}
        >
          <Select
            options={TRIGGER_TYPE_OPTIONS.map((v) => ({
              value: v,
              label: t(`workflows.trigger_type_options.${v}`),
            }))}
          />
        </Form.Item>

        <Form.Item label={t('workflows.field.tags')} name="tags">
          <Select mode="tags" placeholder={t('workflows.field.tags')} />
        </Form.Item>

        {/* Dynamic Steps */}
        <Card
          title={t('workflows.field.steps')}
          style={{ marginBottom: 24 }}
          size="small"
        >
          <Form.List name="steps">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <Card
                    key={key}
                    size="small"
                    style={{ marginBottom: 12 }}
                    title={`${t('workflows.field.step')} ${name}`}
                    extra={
                      <Button
                        type="text"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => remove(name)}
                      />
                    }
                  >
                    <Space size="large" wrap style={{ width: '100%' }}>
                      <Form.Item
                        {...restField}
                        label={t('workflows.field.name')}
                        name={[name, 'name']}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Input
                          placeholder={t('workflows.field.name')}
                          maxLength={200}
                          style={{ width: 200 }}
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        label={t('workflows.field.step_type')}
                        name={[name, 'type']}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          style={{ width: 140 }}
                          options={STEP_TYPE_OPTIONS.map((v) => ({
                            value: v,
                            label: t(`workflows.step_type_options.${v}`),
                          }))}
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        label={t('workflows.field.step_action')}
                        name={[name, 'action']}
                        rules={[{ required: true }]}
                        style={{ marginBottom: 8 }}
                      >
                        <Select
                          style={{ width: 160 }}
                          options={STEP_ACTION_OPTIONS.map((v) => ({
                            value: v,
                            label: v,
                          }))}
                          showSearch
                          allowClear
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        label={t('workflows.field.on_success')}
                        name={[name, 'on_success']}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          min={0}
                          placeholder={t('workflows.field.step')}
                          style={{ width: 100 }}
                        />
                      </Form.Item>

                      <Form.Item
                        {...restField}
                        label={t('workflows.field.on_failure')}
                        name={[name, 'on_failure']}
                        style={{ marginBottom: 8 }}
                      >
                        <InputNumber
                          min={0}
                          placeholder={t('workflows.field.step')}
                          style={{ width: 100 }}
                        />
                      </Form.Item>
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ type: 'action', action: 'send_email' })}
                  block
                  icon={<PlusOutlined />}
                >
                  {t('workflows.action.add_step')}
                </Button>
              </>
            )}
          </Form.List>
        </Card>

        <Form.Item>
          <Space>
            <Button type="primary" htmlType="submit" loading={submitting}>
              {t('app.save')}
            </Button>
            <Button
              onClick={() =>
                navigate(isEdit ? `/workflows/definitions/${id}` : '/workflows')
              }
            >
              {t('app.cancel')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}
